// src/features/settings/components/scanners-panel/ScannersPanel.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Chip, IconButton, Paper, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useScanners } from '../../../inventory/hooks/useScanners';
import { useStockLocations } from '../../../inventory/hooks/useStockLocations';
import { useScannerPermissions } from '../../../auth/hooks/usePermissions';
import { useSnackbar } from '../../../../hooks/mui/useSnackBar';
import { useConfirmDialog } from '../../../../hooks/mui/useConfirmDialog';
import ScannerFormDialog from './ScannerFormDialog';

export default function ScannersPanel() {
  const { t } = useTranslation(['settings', 'common', 'errors']);
  const { showSuccess, showError } = useSnackbar();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const { canCreate, canUpdate, canDelete } = useScannerPermissions();
  const { scanners, loading, error, clearError, fetchScanners, createScanner, updateScanner, deleteScanner } = useScanners();
  const { stockLocations, fetchStockLocations } = useStockLocations();

  // null = closed, {} = create, scanner = edit
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    fetchScanners();
    fetchStockLocations();
  }, [fetchScanners, fetchStockLocations]);

  const errorText = useCallback(
    (result) => (result.errorCode ? t(`errors:${result.errorCode}`, { defaultValue: result.error }) : result.error),
    [t],
  );

  const handleSave = async (values) => {
    const isEdit = Boolean(editing?.scanner_id);
    const payload = {
      name: values.name.trim(),
      default_location_id: values.default_location_id || null,
      require_staff_login: values.require_staff_login,
    };
    if (values.password) payload.password = values.password;

    const result = isEdit
      ? await updateScanner(editing.scanner_id, payload)
      : await createScanner(payload);

    if (!result.success) return errorText(result);
    showSuccess(t(isEdit ? 'settings:scanners.success.updated' : 'settings:scanners.success.created'));
    setEditing(null);
    return null;
  };

  const handleDelete = useCallback((row) => {
    showConfirmDialog({
      title: t('settings:scanners.confirm.delete_title'),
      message: t('settings:scanners.confirm.delete_message', { name: row.name }),
      severity: 'error',
      onConfirm: async () => {
        const result = await deleteScanner(row.scanner_id);
        if (result.success) showSuccess(t('settings:scanners.success.deleted'));
        else showError(errorText(result));
      },
    });
  }, [showConfirmDialog, deleteScanner, showSuccess, showError, errorText, t]);

  const columns = useMemo(() => [
    { field: 'name', headerName: t('settings:scanners.grid.name'), flex: 1, minWidth: 160 },
    {
      field: 'default_location_name',
      headerName: t('settings:scanners.grid.default_location'),
      flex: 1,
      minWidth: 160,
      renderCell: (params) => params.value || '-',
    },
    {
      field: 'require_staff_login',
      headerName: t('settings:scanners.grid.staff_login'),
      width: 170,
      sortable: false,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value ? t('settings:scanners.grid.staff_login_required') : t('settings:scanners.grid.staff_login_off')}
          color={params.value ? 'primary' : 'default'}
        />
      ),
    },
    {
      field: 'actions',
      renderHeader: () => (<></>),
      width: 96,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          {canUpdate && (
            <IconButton size="small" onClick={() => setEditing(params.row)} title={t('common:buttons.edit')}>
              <EditIcon fontSize="small" />
            </IconButton>
          )}
          {canDelete && (
            <IconButton size="small" color="error" onClick={() => handleDelete(params.row)} title={t('common:buttons.delete')}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    },
  ], [t, canUpdate, canDelete, handleDelete]);

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary" paragraph>
          {t('settings:scanners.description')}
        </Typography>

        <Box mb={2} display="flex" gap={1}>
          {canCreate && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing({})}>
              {t('settings:scanners.buttons.add')}
            </Button>
          )}
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => fetchScanners()}>
            {t('common:buttons.refresh')}
          </Button>
        </Box>

        {error && !editing && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
            {error}
          </Alert>
        )}

        <DataGrid
          rows={scanners}
          columns={columns}
          getRowId={(row) => row.scanner_id}
          loading={loading && !editing}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          disableColumnFilter
          disableColumnMenu
          disableColumnResize
          disableRowSelectionOnClick
          localeText={{ noRowsLabel: t('settings:scanners.empty') }}
          sx={{ minHeight: 300 }}
        />
      </Paper>

      <ScannerFormDialog
        open={Boolean(editing)}
        scanner={editing}
        locations={stockLocations.filter(l => l.active || l.stock_location_id === editing?.default_location_id)}
        saving={loading}
        onClose={() => setEditing(null)}
        onSave={handleSave}
      />
      <ConfirmDialog />
    </Box>
  );
}
